import {createContext, PropsWithChildren, useCallback, useContext, useMemo, useState} from "react";
import {useSelf} from "../agent/useSelf.ts";
import {useTranslation} from "react-i18next";
import {Box, Typography} from "@mui/joy";
import {faCalendarClock, faGears, faUserClock} from "@fortawesome/pro-regular-svg-icons";
import {iconForEntityType} from "../fa.tsx";
import {useDockedLocation} from "../driver/activeTasks.ts";
import {VenueCapsule} from "../venue/capsule.tsx";
import {useMountedTruck} from "../driver/mountedTruck.ts";
import {FontAwesomeIconProps} from "@fortawesome/react-fontawesome";
import i18next from "i18next";
import {isSameLink, Snaplink, useRouterSnaplink} from "../routing/useRouterSnaplink.tsx";
import {useSystemConfig} from "../rpc/sysconf.tsx";

export interface NaviItemProps extends PropsWithChildren<Snaplink> {
    icon?: FontAwesomeIconProps;
}

export interface NaviBlockProps {
    subheader: string;
    itemProps: NaviItemProps[];
}

// export class BookmarkMunger {
//     constructor(public setter?: (munger: (prev: NaviItemProps[]) => NaviItemProps[]) => void) {
//     }
//
//     public add() {
//         if (this.setter) {
//             this.setter((prev) => {
//                 // don't want to get tied down with hooks here.
//                 // router.state is not reactive, and that's OK.
//                 const routerState = GLOBAL_ROUTER.state;
//                 const matches = routerState.matches;
//                 const deepestMatch = matches[matches.length - 1];
//                 const newSnaplink: Snaplink = {
//                     // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
//                     to: deepestMatch.pathname as any,
//                 };
//                 return [...prev, newSnaplink];
//             });
//         }
//     }
//
//     public remove(targetFinder: (maybe: NaviItemProps) => boolean) {
//         if (this.setter) {
//             this.setter((prev: NaviItemProps[]) => {
//                 const rv = prev.filter((item: NaviItemProps) => !targetFinder(item));
//                 return rv;
//             });
//         }
//     }
// }

export interface NaviState {
    blocks: NaviBlockProps[];
    active?: NaviItemProps | null;
}

const blankNaviState: NaviState = {blocks: []};


// function useBookmarks() {
//     const [bookmarks, setBookmarks]
//         = useState<NaviItemProps[]>([]);
//     // const [bookmarkMunger]
//     //     = useState<BookmarkMunger>(new BookmarkMunger(setBookmarks));
//     const [rv, setRv]
//         = useState<{ bookmarks: NaviItemProps[], bookmarkMunger: BookmarkMunger }>({
//         bookmarks, bookmarkMunger,
//     });
//     useEffect(() => {
//         setRv({bookmarks, bookmarkMunger});
//     }, [bookmarkMunger, bookmarks]);
//     return rv;
// }
//
// function useBookmarkBlock() {
//     const {t} = useTranslation();
//     const {bookmarks} = useBookmarks();
//     const [bookmarkBlock, setBookmarkBlock]
//         = useState<NaviBlockProps>({
//         subheader: t("navi.bookmarkBlockHeader"),
//         itemProps: [],
//     });
//     useEffect(() => {
//         setBookmarkBlock((prev) => {
//             return {subheader: prev.subheader, itemProps: bookmarks};
//         })
//     }, [bookmarks]);
//     return bookmarkBlock;
// }
//
// function useBookmarkMunger() {
//     const {bookmarkMunger} = useBookmarks();
//     return bookmarkMunger;
// }

function usePermanentBlocks() {
    const {t} = useTranslation();

    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;
    const dockedLocationTqrv = useDockedLocation();
    const dockedLocation = dockedLocationTqrv.data;
    const syscfgTqrv = useSystemConfig();

    const rv = useMemo(() => {
                const rv: NaviBlockProps[] = [];

                if (self?.canAdmin) {
                    rv.push({
                        subheader: t("admin.manage"),
                        itemProps: [
                            {
                                to: "/upload/shinwa-job",
                                children: <Typography>{t("navi.admin.shinwaJobs")}</Typography>
                            },
                            {
                                to: "/admin/sanyu-job",
                                children: <Typography>{t("navi.admin.sanyuJobs")}</Typography>
                            },
                            {
                                to: "/admin/active-agents",
                                children: <Typography>{t("navi.admin.agentPresences")}</Typography>
                            },
                            {
                                to: "/admin/masters",
                                children: <Typography>{t("navi.admin.masters")}</Typography>
                            },
                            {
                                to: "/admin/orphaned-bundle",
                                children: <Typography>{t("navi.admin.orphaned-bundles")}</Typography>
                            },
                            {
                                to: "/admin/job-export",
                                children: <Typography>{t("navi.admin.export-bundles")}</Typography>
                            },
                            {
                                to: "/self/timeline",
                                children: <Box>{t("navi.selfTimeline")}</Box>,
                                icon: {icon: faUserClock},
                            },
                            {
                                to: "/admin/sysconf",
                                children: <Box>{t("navi.admin.systemConfig")}</Box>,
                                icon: {icon: faGears},
                            },
                            {
                                to: "/admin/rewind",
                                children: <Typography>{t("navi.admin.rewind")}</Typography>
                            },
                        ]
                    })
                }

                if (/*import.meta.env.DEV &&*/ self?.canDispatch) {
                    rv.push({
                        subheader: t("navi.dispatch.section"),
                        itemProps: [
                            {
                                to: "/dispatch/bundle",
                                children: <Typography>{t("navi.dispatch.overall")}</Typography>,
                            },
                        ],
                    });
                }

                if (self?.canDrive) {
                    const block: NaviBlockProps = {
                        subheader: t("navi.driverBlockHeader"),
                        itemProps: [
                            {
                                to: "/driver/schedule",
                                children: <Typography>{t("navi.driver.schedule")}</Typography>,
                                icon: {icon: faCalendarClock},
                            },
                            {
                                to: "/self/timeline",
                                children: <Box>{t("navi.selfTimeline")}</Box>,
                                icon: {icon: faUserClock},
                            },
                        ]
                    };

                    block.itemProps.push({
                        to: "/driver/my-truck",
                        children: mountedTruck?.displayName ?? t("unset"),
                        icon: iconForEntityType("truck"),
                    });

                    if (dockedLocation) {
                        block.itemProps.unshift({
                            to: "/venue/$id",
                            params: {id: dockedLocation.id},
                            children: dockedLocation.displayName,
                            icon: iconForEntityType("venue"),
                        });
                    }

                    rv.push(block);
                }

                if (self?.admitterFor) {
                    const block: NaviBlockProps = {
                        subheader: t("navi.admitterBlockHeader"),
                        itemProps: [
                            {
                                to: "/venue/$id/gate",
                                params: {id: self.admitterFor.id},
                                children: <VenueCapsule venue={self.admitterFor}/>,
                                icon: iconForEntityType("admitter"),
                            },
                            {
                                to: "/upload/shinwa-job",
                                children: <Typography>{t("navi.admin.shinwaJobs")}</Typography>
                            },
                        ]
                    };
                    rv.push(block);
                }

                const inspectorFor = self?.inspectorFor ?? self?.supervisorFor;
                if (inspectorFor) {
                    const block: NaviBlockProps = {
                        subheader: t("navi.inspectorBlockHeader"),
                        itemProps: [
                            {
                                to: "/venue/$id/inspect/bundle",
                                params: {id: inspectorFor.id},
                                children: <VenueCapsule venue={{...inspectorFor, icon: iconForEntityType("bundle")}}/>,
                                icon: iconForEntityType("inspector"),
                            },
                        ]
                    };
                    if (syscfgTqrv.data?.showInspectorFacets) {
                        block.itemProps.push({
                            to: "/venue/$id/inspect/waste",
                            params: {id: inspectorFor.id},
                            children: <VenueCapsule venue={{...inspectorFor, icon: iconForEntityType("waste")}}/>,
                            icon: iconForEntityType("inspector"),
                        });
                    }

                    rv.push(block);
                }

                return rv;
            }
            ,
            [dockedLocation, mountedTruck?.displayName, self?.admitterFor, self?.canAdmin,
                self?.canDispatch, self?.canDrive, self?.inspectorFor, self?.supervisorFor, t]
        )
    ;

    return rv;
}

export interface UseScratchNaviBlockResult {
    block: NaviBlockProps;
    dispatchItem: (next: NaviItemProps | null) => void;
}

const blankScratchBlock = {itemProps: [], subheader: i18next.t("navi.scratch")};

function useScratchNaviBlock() {
    const {t} = useTranslation();
    const [state, setState] = useState<NaviBlockProps>(blankScratchBlock);
    const dispatchItem = useCallback((next: NaviItemProps | null) => {
        setState(prev => {
            const rv = !next
                ? (prev.itemProps.length > 0
                    ? blankScratchBlock
                    : prev)
                : (prev.itemProps.length === 0 || !prev.itemProps[0] || !isSameLink(prev.itemProps[0], next)
                    ? ({subheader: t("navi.scratch"), itemProps: [next]})
                    : prev);
            return rv;
        });
    }, [t]);

    // useWhatChanged([dispatchItem, state]);
    const rv = useMemo(() => {
        return {block: state, dispatchItem};
    }, [dispatchItem, state]);

    return rv;
}

//
// function useNaviBlocks() {
//     const permanentBlocks = usePermanentBlocks();
//     const {block: scratchBlock} = useScratchNaviBlock();
//     // const bookmarkBlock = useBookmarkBlock();
//     // const [rv, setRv]
//     //     = useState<NaviBlockProps[]>([...permanentBlocks]);
//     // useWhatChanged([permanentBlocks, bookmarkBlock]);
//     // useEffect(() => {
//     //     setRv([...permanentBlocks]);
//     // }, [permanentBlocks]);
//
//     const rv = useMemo(() => {
//         const rv = [...permanentBlocks, scratchBlock];
//         return rv;
//     }, [permanentBlocks, scratchBlock]);
//
//     return rv;
// }
//
// export function useActiveNaviItem() {
//     const naviBlocks = useNaviBlocks();
//     const [activeNaviItem, setActiveNaviItem]
//         = useState<NaviItemProps | null | undefined>(null);
//     const routerSnaplink = useRouterSnaplink();
//     // useWhatChanged([activeNaviItem, naviBlocks, routerSnaplink]);
//     useEffect(() => {
//         if (!activeNaviItem || !isSameLink(activeNaviItem, routerSnaplink)) {
//             for (const maybeBlock of naviBlocks) {
//                 for (const maybeItem of maybeBlock.itemProps) {
//                     if (isSameLink(maybeItem, routerSnaplink)) {
//                         setActiveNaviItem(maybeItem);
//                         return;
//                     }
//                 }
//             }
//             // we have something random in scratch. it is essential not to allow
//             // whatever our active item was before to remain in here. that way
//             // the backup code showing the trigger will not work correctly, if
//             // the previous selection had an explicit label.
//
//             setActiveNaviItem(null);
//         }
//     }, [activeNaviItem, naviBlocks, routerSnaplink]);
//
//     return activeNaviItem;
// }

const NaviContext = createContext<NaviState>(blankNaviState);
const ScratchBlockDispatchContext =
    createContext<(next: NaviItemProps | null) => void>(() => {/**/
    });

export function useNaviState() {
    return useContext(NaviContext);
}

export function useNaviScratchBlockDispatch() {
    return useContext(ScratchBlockDispatchContext);
}

export function NaviProvider({children}: PropsWithChildren) {
    const permanentBlocks = usePermanentBlocks();
    const scratchBlock = useScratchNaviBlock();
    // useWhatChanged([permanentBlocks, scratchBlock.block]);
    const allBlocks = useMemo(() => {
        const rv = [...permanentBlocks, scratchBlock.block];
        return rv;
    }, [permanentBlocks, scratchBlock.block]);
    const routerSnaplink = useRouterSnaplink();
    const activeItem = useMemo(() => {
        if (routerSnaplink) {
            for (const maybeBlock of allBlocks) {
                for (const maybeItem of maybeBlock.itemProps) {
                    if (isSameLink(maybeItem, routerSnaplink)) {
                        return maybeItem;
                    }
                }
            }
        }
        return null;
    }, [allBlocks, routerSnaplink]);
    const state = useMemo(() => {
        const rv: NaviState = {blocks: allBlocks, active: activeItem};
        return rv;
    }, [activeItem, allBlocks]);

    return <NaviContext value={state}>
        <ScratchBlockDispatchContext value={scratchBlock.dispatchItem}>
            {children}
        </ScratchBlockDispatchContext>
    </NaviContext>
}
